import React, { useEffect, useState } from 'react';
import axios from '../../utils/axiosConfig';
import { Box, Typography, Button, TextField, Grid, Card, CardContent, CardActions, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Tooltip, IconButton, Alert } from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';

const API = '/api/credentials';
const TENANTS = '/api/tenants';

const defaultConfigByProvider = (p) => {
  if (p === 'shopify_storefront') return { shop_url: '', storefront_access_token: '', webhook_secret: '' };
  if (p === 'openrouter') return { api_key: '' };
  return { api_key: '' };
};

const Credentials = () => {
  const [creds, setCreds] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    tenant_id: '',
    provider: 'openai',
    name: '',
    configJson: '{"api_key":""}',
    shopUrl: '',
    storefrontToken: '',
    webhookSecret: '',
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [c, t] = await Promise.all([axios.get(`${API}/`), axios.get(`${TENANTS}/`)]);
    setCreds(c.data);
    setTenants(t.data);
  };
  useEffect(() => {
    load();
  }, []);

  const buildConfig = () => {
    const p = form.provider;
    if (p === 'shopify_storefront') {
      return {
        shop_url: (form.shopUrl || '').trim(),
        storefront_access_token: (form.storefrontToken || '').trim(),
        webhook_secret: (form.webhookSecret || '').trim(),
      };
    }
    return JSON.parse(form.configJson || '{}');
  };

  const create = async () => {
    setSaving(true);
    try {
      const config = buildConfig();
      const payload = {
        tenant_id: Number(form.tenant_id),
        name: form.name || form.provider,
        provider: form.provider,
        config,
        is_active: true,
      };
      await axios.post(`${API}/`, payload);
      setOpen(false);
      setForm({
        tenant_id: '',
        provider: 'openai',
        name: '',
        configJson: '{"api_key":""}',
        shopUrl: '',
        storefrontToken: '',
        webhookSecret: '',
      });
      await load();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || e.message || 'Fehler');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Credential wirklich löschen?')) return;
    await axios.delete(`${API}/${id}`);
    await load();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h4">API-Credentials</Typography>
          <Tooltip
            title="LLM-Keys (OpenAI, Anthropic, OpenRouter) und Shopify Storefront API für Wissens-Import. Keys werden verschlüsselt gespeichert."
            placement="right"
            arrow
          >
            <IconButton size="small" sx={{ color: '#FFD700' }}>
              <InfoIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Button variant="contained" onClick={() => setOpen(true)} sx={{ fontWeight: 600 }}>
          Neu
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Für den öffentlichen Tenant-Chat muss der Provider zur Modellkonfiguration passen (z. B. <code>openai</code> + OpenAI-Key).
        Shopify: <code>shopify_storefront</code> mit Shop-URL und Storefront Access Token (Admin → Apps → Dev).
      </Alert>

      <Grid container spacing={2}>
        {creds.map((c) => (
          <Grid item xs={12} md={6} lg={4} key={c.id}>
            <Card>
              <CardContent>
                <Typography variant="h6">{c.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Provider: {c.provider}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Tenant #{c.tenant_id}
                </Typography>
              </CardContent>
              <CardActions>
                <Button size="small" color="error" onClick={() => remove(c.id)}>
                  Löschen
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Neue Credentials</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select
              label="Mandant"
              value={form.tenant_id}
              onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}
              fullWidth
              required
            >
              {tenants.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name} ({t.slug})
                </MenuItem>
              ))}
            </TextField>
            <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth placeholder="z. B. Produktion OpenAI" />
            <TextField
              select
              label="Provider"
              value={form.provider}
              onChange={(e) => {
                const p = e.target.value;
                setForm({
                  ...form,
                  provider: p,
                  configJson: JSON.stringify(defaultConfigByProvider(p), null, 2),
                });
              }}
              fullWidth
            >
              <MenuItem value="openai">openai</MenuItem>
              <MenuItem value="anthropic">anthropic</MenuItem>
              <MenuItem value="openrouter">openrouter</MenuItem>
              <MenuItem value="shopify_storefront">shopify_storefront (Storefront API)</MenuItem>
              <MenuItem value="ollama">ollama</MenuItem>
            </TextField>

            {form.provider === 'shopify_storefront' ? (
              <>
                <TextField
                  label="Shop-URL (myshopify oder Primärdomain)"
                  value={form.shopUrl}
                  onChange={(e) => setForm({ ...form, shopUrl: e.target.value })}
                  fullWidth
                  placeholder="https://ihr-shop.myshopify.com"
                />
                <TextField
                  label="Storefront Access Token"
                  value={form.storefrontToken}
                  onChange={(e) => setForm({ ...form, storefrontToken: e.target.value })}
                  fullWidth
                  type="password"
                  autoComplete="new-password"
                />
                <TextField
                  label="Webhook Secret (optional, Custom App API secret)"
                  value={form.webhookSecret}
                  onChange={(e) => setForm({ ...form, webhookSecret: e.target.value })}
                  fullWidth
                  type="password"
                  autoComplete="new-password"
                  helperText="Für HMAC an https://…/api/integrations/shopify/webhook — gleicher Secret wie in der Shopify Custom App."
                />
              </>
            ) : (
              <TextField
                label="Konfiguration (JSON)"
                value={form.configJson}
                onChange={(e) => setForm({ ...form, configJson: e.target.value })}
                fullWidth
                multiline
                minRows={6}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Abbrechen</Button>
          <Button onClick={create} disabled={saving || !form.tenant_id} variant="contained">
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Credentials;
